/**
 * The selectable text layer over a photo — what a PDF viewer does with a scanned page.
 *
 * Each recognised word is an absolutely-positioned, transparent span over the pixels it came from,
 * so a drag highlights the words under it and the browser's own selection/copy (and a phone's
 * long-press handles) work. It is rendered in the SAME transformed box as the photo (the viewer
 * gives it the photo's rotate/zoom), so it follows zoom, rotation and window resizes.
 *
 * OCR text is untrusted: it is ONLY ever rendered as React text children (escaped), never as HTML.
 *
 * Copy is overridden so the clipboard gets reading-order text (spaces within a line, newline
 * between lines) instead of one word per line.
 */
import { useRef, type ClipboardEvent } from 'react';
import { useBoxSize } from '../../../FieldService/gallery/markup/useBoxSize';
import { joinWordsForCopy } from './wordText';
import type { OcrResponse, OcrWord } from './ocrTypes';

let measureCtx: CanvasRenderingContext2D | null | undefined;
/** Width of `text` in px at `fontPx`, or null where canvas is unavailable (then the box is not stretched). */
function measure(text: string, fontPx: number): number | null {
  if (measureCtx === undefined) {
    try {
      measureCtx = typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null;
    } catch {
      measureCtx = null;
    }
  }
  if (!measureCtx) return null;
  measureCtx.font = `${fontPx}px sans-serif`;
  return measureCtx.measureText(text).width;
}

function spanStyle(word: OcrWord, boxW: number): React.CSSProperties {
  const widthPx = Math.max(1, word.w * boxW);
  const heightPx = Math.max(1, word.h * boxW);
  const fontPx = Math.max(4, heightPx * 0.8);
  const natural = measure(word.text, fontPx);
  const scaleX = natural && natural > 0 ? widthPx / natural : 1;
  return {
    position: 'absolute',
    left: `${word.x * 100}%`,
    top: `${word.y * 100}%`,
    width: `${natural ? natural : widthPx}px`,
    height: `${heightPx}px`,
    fontSize: `${fontPx}px`,
    lineHeight: `${heightPx}px`,
    fontFamily: 'sans-serif',
    whiteSpace: 'pre',
    transformOrigin: '0 0',
    transform: `rotate(${word.angle}deg) scaleX(${scaleX})`,
    color: 'transparent',
    cursor: 'text',
    userSelect: 'text',
    WebkitUserSelect: 'text',
  };
}

export function OcrTextLayer({ result }: { result: OcrResponse }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const box = useBoxSize(rootRef);
  const aspect = result.imageW > 0 ? result.imageH / result.imageW : 1;

  const onCopy = (e: ClipboardEvent<HTMLDivElement>) => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) return;
    const chosen = result.words.filter((_, i) => {
      const el = rootRef.current?.children[i];
      return !!el && sel.containsNode(el, true);
    });
    if (chosen.length === 0) return;
    e.clipboardData.setData('text/plain', joinWordsForCopy(chosen, aspect));
    e.preventDefault();
  };

  return (
    <div
      ref={rootRef}
      data-testid="lightbox-ocr-layer"
      // Up to 2,000 transparent word spans: noise for a screen reader. "Copy all" is the accessible route.
      aria-hidden="true"
      className="absolute inset-0 selection:bg-[#3FA5FF]/40"
      onCopy={onCopy}
    >
      {result.words.map((w, i) => (
        <span key={i} data-ocr-word style={spanStyle(w, box.w)}>
          {w.text}
        </span>
      ))}
    </div>
  );
}
